// ============================================================================
// SUB-PROCESS CARD - Componente Visual para Fluxos Aninhados (Super Card)
// ============================================================================
//
// Este componente renderiza um card especial que representa um fluxo inteiro.
// Características visuais:
// - Borda dupla para indicar que é um "container" de fluxo
// - Ícone de link/layers para indicar aninhamento
// - Preview de métricas agregadas (LT, TT, Eficiência)
// - Double-click para drill-down (navegar para o fluxo filho)
// - Badge com número de atividades contidas
//
// ============================================================================

import React, { useState, useCallback, useMemo } from 'react';
import { CardData } from '../types';
import { 
  Layers, 
  ExternalLink, 
  ChevronRight, 
  Clock, 
  Activity,
  AlertTriangle,
  Link2,
  Maximize2,
  Minimize2
} from 'lucide-react';
import { 
  formatTotalTime, 
  getSubProcessSummary,
  isGroupedCard,
  canDrillDown,
  getDrillDownInfo
} from '../utils/nestedFlowCalculations';

// ============================================================================
// TIPOS
// ============================================================================

interface SubProcessCardProps {
  data: CardData;
  selected: boolean;
  cardWidth: number;
  cardHeight: number;
  onDoubleClick?: () => void;
  onEdit?: () => void;
  onUngroup?: () => void;
  onToggleExpand?: (cardId: string, expanded: boolean) => void;
  isExpanded?: boolean;
  onNavigateToFlow?: (projectId: string, sheetId: string) => void;
  scale?: number;
  opacity?: number;
  isBottleneck?: boolean;
}

// ============================================================================
// CORES E ESTILOS
// ============================================================================

const SUB_PROCESS_COLORS = {
  primary: '#6366f1', // Indigo-500
  secondary: '#818cf8', // Indigo-400
  background: '#eef2ff', // Indigo-50
  backgroundHover: '#e0e7ff', // Indigo-100
  border: '#a5b4fc', // Indigo-300
  borderDouble: '#6366f1', // Indigo-500
  text: '#312e81', // Indigo-900
  textMuted: '#6366f1', // Indigo-500
  badge: '#4f46e5', // Indigo-600
  linked: '#22c55e', // Green-500 (quando vinculado a fluxo externo)
  grouped: '#f59e0b', // Amber-500 (quando criado por agrupamento)
};

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export const SubProcessCard: React.FC<SubProcessCardProps> = ({
  data,
  selected,
  cardWidth,
  cardHeight,
  onDoubleClick,
  onEdit,
  onUngroup,
  onToggleExpand,
  isExpanded = false,
  onNavigateToFlow,
  scale = 1,
  opacity = 1,
  isBottleneck = false,
}) => {
  const [isHovered, setIsHovered] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);

  // Determinar tipo de sub-processo
  const isLinkedToExternal = canDrillDown(data);
  const isFromGrouping = isGroupedCard(data);
  const drillDownInfo = isLinkedToExternal ? getDrillDownInfo(data) : null;

  // Dados do sub-processo
  const subData = data.subProcessData;
  const activityCount = subData?.activityCount || data.groupedCardIds?.length || 0;
  const totalLeadTime = subData?.totalLeadTime || (data.lt ? data.lt * 24 * 60 : 0);
  const totalTouchTime = subData?.totalTouchTime || data.tt || 0;
  const efficiency = subData?.efficiency || 
    (totalLeadTime > 0 ? (totalTouchTime / totalLeadTime) * 100 : 0);
  const hasBottleneck = subData?.hasBottleneck || false;

  // Estilo da borda baseado no tipo
  const borderColor = useMemo(() => {
    if (isBottleneck) return '#ef4444'; // Red
    if (selected) return SUB_PROCESS_COLORS.borderDouble;
    if (isLinkedToExternal) return SUB_PROCESS_COLORS.linked;
    if (isFromGrouping) return SUB_PROCESS_COLORS.grouped;
    return SUB_PROCESS_COLORS.border;
  }, [selected, isLinkedToExternal, isFromGrouping, isBottleneck]);

  // Handler de double-click para drill-down
  const handleDoubleClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    if (onDoubleClick) {
      onDoubleClick();
    } else if (isLinkedToExternal && drillDownInfo && onNavigateToFlow) {
      onNavigateToFlow(drillDownInfo.projectId, drillDownInfo.sheetId);
    }
  }, [onDoubleClick, isLinkedToExternal, drillDownInfo, onNavigateToFlow]);

  // Tooltip com resumo
  const tooltipContent = useMemo(() => getSubProcessSummary(data), [data]);

  return (
    <div
      className="sub-process-card"
      style={{
        width: cardWidth,
        height: cardHeight,
        opacity,
        position: 'relative',
        cursor: 'pointer',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => {
        setIsHovered(false);
        setShowTooltip(false);
      }}
      onDoubleClick={handleDoubleClick}
    >
      {/* Borda Dupla Externa (efeito visual de container) */}
      <div
        className="absolute inset-0 rounded"
        style={{
          border: `3px solid ${borderColor}`,
          borderRadius: 12,
          boxShadow: selected 
            ? `0 0 0 2px ${SUB_PROCESS_COLORS.primary}40, 0 4px 12px rgba(0,0,0,0.15)`
            : isHovered 
              ? '0 4px 12px rgba(0,0,0,0.1)'
              : '0 2px 4px rgba(0,0,0,0.05)',
          transition: 'all 0.2s ease',
        }}
      />

      {/* Borda Interna (efeito de dupla borda) */}
      <div
        className="absolute rounded"
        style={{
          top: 4,
          left: 4,
          right: 4,
          bottom: 4,
          border: `1px dashed ${borderColor}50`,
          borderRadius: 8,
        }}
      />

      {/* Container Principal */}
      <div
        className="absolute flex flex-col overflow-hidden"
        style={{
          top: 6,
          left: 6,
          right: 6,
          bottom: 6,
          backgroundColor: isHovered ? SUB_PROCESS_COLORS.backgroundHover : SUB_PROCESS_COLORS.background,
          borderRadius: 6,
          transition: 'background-color 0.2s ease',
        }}
      >
        {/* Header com Ícone e Título */}
        <div
          className="flex items-center gap-2 px-3 py-2"
          style={{
            backgroundColor: `${borderColor}20`,
            borderBottom: `1px solid ${borderColor}30`,
          }}
        >
          {/* Ícone de Tipo */}
          {isLinkedToExternal ? (
            <Link2 size={16} color={SUB_PROCESS_COLORS.linked} />
          ) : (
            <Layers size={16} color={SUB_PROCESS_COLORS.primary} />
          )}

          {/* Título */}
          <span
            className="flex-1 font-medium truncate"
            style={{
              color: SUB_PROCESS_COLORS.text,
              fontSize: 12,
            }}
          >
            {data.atividade || subData?.flowName || 'Sub-processo'}
          </span>

          {/* Badge de Atividades */}
          <span
            className="px-2 py-0.5 rounded-sm text-xs font-bold"
            style={{
              backgroundColor: SUB_PROCESS_COLORS.badge,
              color: 'white',
            }}
          >
            {activityCount}
          </span>
        </div>

        {/* Corpo com Métricas */}
        <div className="flex-1 flex flex-col justify-center px-3 py-2 gap-1">
          {/* Lead Time */}
          <div className="flex items-center justify-between text-xs">
            <span className="flex items-center gap-1" style={{ color: SUB_PROCESS_COLORS.textMuted }}>
              <Clock size={12} />
              Lead Time
            </span>
            <span style={{ color: SUB_PROCESS_COLORS.text, fontWeight: 600 }}>
              {formatTotalTime(totalLeadTime)}
            </span>
          </div>

          {/* Touch Time */}
          <div className="flex items-center justify-between text-xs">
            <span className="flex items-center gap-1" style={{ color: SUB_PROCESS_COLORS.textMuted }}>
              <Activity size={12} />
              Touch Time
            </span>
            <span style={{ color: SUB_PROCESS_COLORS.text, fontWeight: 600 }}>
              {formatTotalTime(totalTouchTime)}
            </span>
          </div>

          {/* Eficiência */}
          <div className="flex items-center justify-between text-xs mt-1">
            <span style={{ color: SUB_PROCESS_COLORS.textMuted }}>
              Eficiência
            </span>
            <span
              className="px-1.5 py-0.5 rounded"
              style={{
                backgroundColor: efficiency > 50 ? '#dcfce7' : efficiency > 20 ? '#fef9c3' : '#fee2e2',
                color: efficiency > 50 ? '#166534' : efficiency > 20 ? '#854d0e' : '#991b1b',
                fontWeight: 600,
              }}
            >
              {efficiency.toFixed(0)}%
            </span>
          </div>
        </div>

        {/* Footer com Ações */}
        <div
          className="flex items-center justify-between px-3 py-1.5"
          style={{
            backgroundColor: `${borderColor}10`,
            borderTop: `1px solid ${borderColor}20`,
          }}
        >
          {/* Indicador de Tipo */}
          <span className="text-xs" style={{ color: SUB_PROCESS_COLORS.textMuted }}>
            {isLinkedToExternal ? 'x Externo' : isFromGrouping ? 'x Agrupado' : 'x Fluxo'}
          </span>

          {/* Botões de Ação */}
          <div className="flex items-center gap-1">
            {/* Botão de Expandir (quando agrupado/minimizado) */}
            {isFromGrouping && !isExpanded && onToggleExpand && (
              <button
                className="flex items-center gap-1 px-2 py-0.5 rounded hover:bg-white/50 transition-colors"
                style={{ color: SUB_PROCESS_COLORS.grouped, fontSize: 11, fontWeight: 500 }}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleExpand(data.id, true);
                }}
                title="Expandir cards agrupados"
              >
                <Maximize2 size={12} />
                Expandir
              </button>
            )}

            {/* Botão de Reagrupar (quando expandido) */}
            {isFromGrouping && isExpanded && onToggleExpand && (
              <button
                className="flex items-center gap-1 px-2 py-0.5 rounded hover:bg-white/50 transition-colors"
                style={{ color: SUB_PROCESS_COLORS.grouped, fontSize: 11, fontWeight: 500 }}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleExpand(data.id, false);
                }}
                title="Reagrupar cards"
              >
                <Minimize2 size={12} />
                Reagrupar
              </button>
            )}

            {/* Botão de Navegação */}
            {isLinkedToExternal && (
              <button
                className="flex items-center gap-1 px-2 py-0.5 rounded hover:bg-white/50 transition-colors"
                style={{ color: SUB_PROCESS_COLORS.primary, fontSize: 11 }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (drillDownInfo && onNavigateToFlow) {
                    onNavigateToFlow(drillDownInfo.projectId, drillDownInfo.sheetId);
                  }
                }}
              >
                Abrir <ChevronRight size={12} />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Indicador de Gargalo */}
      {(hasBottleneck || isBottleneck) && (
        <div
          className="absolute -top-2 -right-2 p-1 rounded-full"
          style={{
            backgroundColor: '#fee2e2',
            border: '2px solid #ef4444',
          }}
        >
          <AlertTriangle size={12} color="#ef4444" />
        </div>
      )}

      {/* Ícone de Link Externo (canto superior direito) */}
      {isLinkedToExternal && (
        <div
          className="absolute -top-1 -right-1 p-0.5 rounded-full"
          style={{
            backgroundColor: SUB_PROCESS_COLORS.linked,
          }}
        >
          <ExternalLink size={10} color="white" />
        </div>
      )}

      {/* Tooltip com Resumo */}
      {showTooltip && (
        <div
          className="absolute z-50 p-3 rounded shadow-xl"
          style={{
            top: cardHeight + 8,
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: 'white',
            border: `1px solid ${SUB_PROCESS_COLORS.border}`,
            minWidth: 200,
            whiteSpace: 'pre-line',
            fontSize: 12,
            color: SUB_PROCESS_COLORS.text,
          }}
        >
          {tooltipContent}
        </div>
      )}

      {/* Handles de Conexão (simplificados) */}
      <div
        className="absolute w-3 h-3 bg-white border-2 rounded-full"
        style={{
          top: '50%',
          left: -6,
          transform: 'translateY(-50%)',
          borderColor: borderColor,
        }}
      />
      <div
        className="absolute w-3 h-3 bg-white border-2 rounded-full"
        style={{
          top: '50%',
          right: -6,
          transform: 'translateY(-50%)',
          borderColor: borderColor,
        }}
      />
    </div>
  );
};

export default SubProcessCard;


